/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Box, Group, Text, Tooltip, rem, useMantineTheme } from '@mantine/core';
import { RiQuestionLine } from '@remixicon/react';

import { TextVariants } from '@fai/ui/components/typography/variants';
import type { PropsWithChildren } from '@fai/util/types';

import type { PlanTagProps } from './model';

export default function PlanTag({
  children,
  borderColor,
  tooltipWidth,
  tooltipContent,
  showHelp = true,
  ...props
}: PropsWithChildren<PlanTagProps>) {
  const theme = useMantineTheme();

  return (
    <Group gap={theme.pf.layout['4xs']} onClick={(e) => e.stopPropagation()}>
      <Text
        h={theme.pf.layout.sm}
        px={theme.pf.layout['3xs']}
        py={rem(2)}
        bd={`1px solid ${borderColor ?? 'transparent'}`}
        style={{ borderRadius: theme.radius.sm }}
        variant={TextVariants.Title2Xs}
        {...props}
      >
        {children}
      </Text>

      {showHelp && tooltipContent && (
        <Tooltip label={tooltipContent} w={tooltipWidth ?? rem(280)}>
          <Box style={{ lineHeight: 0 }}>
            <RiQuestionLine color={theme.pf.color.fg.neutral.default} size={18} />
          </Box>
        </Tooltip>
      )}
    </Group>
  );
}

PlanTag.displayName = 'PlanTag';
